import React from 'react';
import { Badge } from '../../../components/ui/badge';
import { Pill, ShieldCheck, FileText, Building2 } from 'lucide-react';

/**
 * IntegratedDrugHeader Component
 * Displays drug and plan information
 * Pure content component for use within UnifiedCoverageContainer Card
 * @param {boolean} compact - Use smaller spacing and sizing for sticky header
 */
export const IntegratedDrugHeader = ({ planName, controller, drugMetadata, compact = false }) => {
  if (!planName && !drugMetadata) {
    return null;
  }

  // Adjust spacing based on compact mode
  const gridGap = compact ? "gap-4 sm:gap-6" : "gap-6 sm:gap-8 md:gap-10";
  const sectionSpacing = compact ? "space-y-2 sm:space-y-3" : "space-y-3 sm:space-y-4";
  const iconSize = compact ? "h-3 w-3 sm:h-3.5 sm:w-3.5" : "h-3.5 w-3.5 sm:h-4 sm:w-4";
  const avatarSize = compact ? "h-8 w-8 sm:h-9 sm:w-9" : "h-9 w-9 sm:h-10 sm:w-10 md:h-11 md:w-11";
  const avatarIconSize = compact ? "h-3.5 w-3.5 sm:h-4 sm:w-4" : "h-4 w-4 sm:h-5 sm:w-5";
  const itemGap = compact ? "gap-2" : "gap-2 sm:gap-3";
  const textSpacing = compact ? "space-y-0.5 sm:space-y-1" : "space-y-1 sm:space-y-2";

  return (
    <div 
      role="region"
      aria-label="Selected plan and drug information"
    >
      <div className={`grid sm:grid-cols-2 ${gridGap}`}>
        {/* Plan Information */}
        {planName && (
          <div className={sectionSpacing}>
            <div className="flex items-center gap-2 text-muted-foreground">
              <ShieldCheck className={iconSize} />
              <span className="text-xs sm:text-sm font-medium tracking-wide">Insurance Plan</span>
            </div>
            <div className={`flex items-start ${itemGap}`}>
              <div className="flex-shrink-0 mt-0.5">
                <div className={`${avatarSize} rounded-full bg-primary/10 flex items-center justify-center ring-2 ring-primary/20`}>
                  <Building2 className={`${avatarIconSize} text-primary`} />
                </div>
              </div>
              <div className={`min-w-0 flex-1 ${textSpacing}`}>
                <h3 className="font-semibold text-sm sm:text-base text-foreground line-clamp-2 leading-snug">
                  {planName}
                </h3>
                {controller && (
                  <p className="text-xs sm:text-sm font-normal text-muted-foreground leading-relaxed line-clamp-1">
                    {controller}
                  </p>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Drug Information */}
        {drugMetadata && (
          <div className={sectionSpacing}>
            <div className="flex items-center gap-2 text-muted-foreground">
              <Pill className={iconSize} />
              <span className="text-xs sm:text-sm font-medium tracking-wide">Drug Name</span>
            </div>
            <div className={`flex items-start ${itemGap}`}>
              <div className="flex-shrink-0 mt-0.5">
                <div className={`${avatarSize} rounded-full bg-primary/10 flex items-center justify-center ring-2 ring-primary/20`}>
                  <FileText className={`${avatarIconSize} text-primary`} />
                </div>
              </div>
              <div className={`min-w-0 flex-1 ${textSpacing}`}>
                <div>
                  <h3 className="font-semibold text-sm sm:text-base text-foreground line-clamp-2 leading-snug">
                    {drugMetadata.drug_description ||
                      drugMetadata.drug_name ||
                      'Drug Name'}
                  </h3>
                  {drugMetadata.drug_name &&
                    drugMetadata.drug_name !== drugMetadata.drug_description && (
                      <p className="text-xs sm:text-sm font-normal text-muted-foreground mt-1 leading-relaxed line-clamp-1">
                        {drugMetadata.drug_name}
                      </p>
                    )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default IntegratedDrugHeader;
